import type { Vehicle, VehicleType } from "@/types";

const KEY = "wp-vehicle-overrides-v1";
const EXTRAS_KEY = "wp-vehicle-extras-v1";

// type/status/color não são mais overridáveis por aqui: tipo/cor do ícone
// vêm de wp.vehicleType/wp.colorMode/wp.color no Traccar (ver
// VehicleIconPicker + vehiclesService.updateIconConfig), e status é sempre
// calculado a partir do Traccar em tempo real — nenhum dos dois é dado
// cadastral do GPS Drive (decisão de produto, ver VEHICLE_FORM_ARCHITECTURE.md).
// "name" saiu da lista 2026-09-01: agora que o nome de verdade vem sempre do
// Traccar (ver vehiclesService.updateName em routes/index.tsx#handleSave),
// um override antigo aqui só serviria para MASCARAR o nome real pra sempre
// nesse navegador — bug relatado ("editei o nome, salvei, não muda no
// visual"): quem tinha um override de "name" de uma versão bem mais antiga
// deste app (dialog legado 100% localStorage) via esse valor zumbi
// sobrepor qualquer nome novo vindo do servidor em todo F5.
const OVERRIDABLE_KEYS = ["plate", "driver", "iconKey", "blocked"] as const;

export type VehicleOverride = Partial<Pick<Vehicle, (typeof OVERRIDABLE_KEYS)[number]>>;

// Filtra por allowlist em runtime, não só por tipo — um localStorage antigo
// (gravado pelo diálogo de cadastro legado, já removido) pode ainda ter
// type/status/color persistidos no navegador de quem testou aquela versão;
// sem este filtro em tempo de execução, esses valores zumbis continuariam
// mascarando o dado real do Traccar mesmo depois desta limpeza.
function sanitizeOverride(raw: VehicleOverride): VehicleOverride {
  const clean: VehicleOverride = {};
  for (const key of OVERRIDABLE_KEYS) {
    if (key in raw) (clean as Record<string, unknown>)[key] = raw[key];
  }
  return clean;
}

type OverridesMap = Record<string, VehicleOverride>;

function safeRead<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function safeWrite(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new Event("wp-vehicle-overrides"));
  } catch {
    /* noop */
  }
}

export function getOverrides(): OverridesMap {
  return safeRead<OverridesMap>(KEY, {});
}
export function setOverride(id: string, patch: VehicleOverride) {
  const cur = getOverrides();
  cur[id] = { ...(cur[id] ?? {}), ...patch };
  safeWrite(KEY, cur);
}
export function getExtras(): Vehicle[] {
  return safeRead<Vehicle[]>(EXTRAS_KEY, []);
}
export function addExtra(v: Vehicle) {
  const list = getExtras();
  list.unshift(v);
  safeWrite(EXTRAS_KEY, list);
}
export function removeExtra(id: string) {
  safeWrite(EXTRAS_KEY, getExtras().filter((v) => v.id !== id));
}

export function mergeVehicleData(list: Vehicle[]): Vehicle[] {
  const ov = getOverrides();
  const extras = getExtras();
  const merged = list.map((v) => (ov[v.id] ? { ...v, ...sanitizeOverride(ov[v.id]) } : v));
  // adiciona extras (locais) que não existam ainda
  const known = new Set(merged.map((v) => v.id));
  for (const e of extras) if (!known.has(e.id)) merged.push({ ...e, ...sanitizeOverride(ov[e.id] ?? {}) });
  return merged;
}

export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  car: "Carro",
  moto: "Moto",
  truck: "Caminhão",
  van: "Van",
  machine: "Máquina",
};
